import { CAPA_WORKFLOW_TYPES } from '@retracedgmbh/modules/capa/contracts';
import type {
  AiProcessingCapImportContext,
  AiProcessingItem,
  AuditProcessingStep,
} from '@/services/api/types/ai-processing';

// Visible audit "stages" in the queue card. Each groups one or more backend
// workflow step keys (CAPA_WORKFLOW_TYPES, imported from @retracedgmbh/modules —
// never bare string literals) into a single user-facing row. The raw file
// `EXTRACTION` step is folded into "Audit details" (it's an internal
// prerequisite of the audit-data extraction, not its own row) — or into "CAP
// extraction" on a CAP-only workflow, which has no audit row at all (see
// `CAP_ONLY_STAGE_PLAN`).
export type AuditStageId = 'auditDetails' | 'capExtraction' | 'findings';

// PROCESSING → running (bar + %); REVIEW → finished + awaiting the user's review
// (amber "Review" row) — for independently-reviewable stages not yet reviewed;
// COMPLETED → finished + shown grey "done", produced for a finished NON-reviewable
// stage, OR a reviewable stage the user has already reviewed (every present member
// step carries a non-null `reviewedAt`); FAILED → the stage the workflow stopped
// at. (A finished reviewable stage stays REVIEW until the backend marks its steps
// reviewed via `reviewedAt`, then flips to COMPLETED.)
export type AuditStageStatus = 'PROCESSING' | 'COMPLETED' | 'REVIEW' | 'FAILED';

export interface AuditStage {
  id: AuditStageId;
  status: AuditStageStatus;
  /** 0–100, averaged across the stage's present member steps. */
  percent: number;
}

// Ordered plan: each stage → the workflow step keys it represents. A stage only
// renders when at least one of its keys is present in `item.steps`, so a no-CAP
// audit never shows a CAP row, and Findings only appears after "Get AI
// suggestions" appends its steps.
//
// `reviewable` = the stage is an independent review entry point: finished →
// amber REVIEW row until its member steps carry `reviewedAt`, then grey COMPLETED.
// `capExtraction` IS reviewable on its own — CAP extraction is an independently-
// tracked review job (per product: flow-2 has audit-details + CAP-extraction as
// two review jobs), so a finished-but-unreviewed CAP stage shows its own Review
// row and flips to Completed once reviewed.
const STAGE_PLAN: { id: AuditStageId; keys: string[]; reviewable: boolean }[] =
  [
    {
      id: 'auditDetails',
      keys: [
        CAPA_WORKFLOW_TYPES.CAPA_CLASSIFICATION,
        CAPA_WORKFLOW_TYPES.EXTRACTION,
      ],
      reviewable: true,
    },
    {
      id: 'capExtraction',
      keys: [CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION],
      reviewable: true,
    },
    {
      id: 'findings',
      keys: [
        CAPA_WORKFLOW_TYPES.EXTRACT_FINDINGS,
        CAPA_WORKFLOW_TYPES.CAPS_CREATION,
      ],
      reviewable: true,
    },
  ];

// Plan for a CAP-ONLY workflow (`POST /capa/process` with just a `capFileId` —
// the CAP-import flow). It runs EXTRACTION + CAPA_CAP_FILE_DATA_EXTRACTION and
// NOTHING else: no classification, no findings. There is no audit to review, so
// the card must not render an "Audit details" row keyed off the raw EXTRACTION
// step (that row would be a permanent dead end — its review step doesn't exist
// in this flow). Instead the whole workflow is ONE user-facing stage, "CAP
// extraction", grouping both steps — the single review job the CAP-import dialog
// resumes into.
const CAP_ONLY_STAGE_PLAN: {
  id: AuditStageId;
  keys: string[];
  reviewable: boolean;
}[] = [
  {
    id: 'capExtraction',
    keys: [
      CAPA_WORKFLOW_TYPES.EXTRACTION,
      CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION,
    ],
    reviewable: true,
  },
];

// A CAP-only workflow: the CAP extraction is planned but classification never
// is. (Both flows carry the raw-file EXTRACTION step, so its presence proves
// nothing — the absence of CAPA_CLASSIFICATION is the discriminator.)
function isCapOnlyWorkflow(byKey: Map<string, AuditProcessingStep>): boolean {
  return (
    byKey.has(CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION) &&
    !byKey.has(CAPA_WORKFLOW_TYPES.CAPA_CLASSIFICATION)
  );
}

/** Whether this queue item is a CAP-only (CAP-import) workflow — see above. */
export function isCapOnlyItem(item: AiProcessingItem): boolean {
  if (item.assetType !== 'AUDIT' || !item.steps || item.steps.length === 0)
    return false;
  return isCapOnlyWorkflow(new Map(item.steps.map(step => [step.key, step])));
}

/**
 * Whether a CAP-only queue item records WHERE its import belongs.
 *
 * A CAP-only workflow creates no audit, so the queue row's `metadata.capImport`
 * (written by `POST /capa/process` for every CAP-only flow — `{}` when the caller
 * supplied nothing) is the only record of the source audit / audited company the
 * CAP-import dialog needs. Rows written before that metadata existed have no
 * `capImport` at all, and CAP workflows started by the backend Dropbox
 * file-accept flow (`dispatchCap`) never record one either.
 *
 * "Recorded" means at least one of `sourceAuditId` / `companyId` is present: the
 * dialog still falls back to the mounting page's props for the other one, so one
 * recovered id is enough to resume with. Missing `capImport`, or a `capImport`
 * with neither id, means there is nothing to resume into.
 */
function hasCapImportContext(item: AiProcessingItem): boolean {
  // `metadata` is typed `Record<string, string>` but is a JSONB bag carrying
  // nested objects at runtime (`file`, `error`, `capImport`) — cast through
  // `unknown`, exactly like QueueCard/ActiveJobsPopover do for `file`/`error`.
  const capImport = (item.metadata as Record<string, unknown> | null)
    ?.capImport as AiProcessingCapImportContext | undefined;
  return !!capImport && (!!capImport.sourceAuditId || !!capImport.companyId);
}

/**
 * Whether this item is a CAP-only import that CANNOT be reviewed from the queue:
 * its CAP stage is finished and unreviewed, but the row never recorded the audit
 * and company the import belongs to (see {@link hasCapImportContext}).
 *
 * Those ids are chosen BEFORE the analysis starts and are not re-decidable at
 * review time, so such a job has no review to open: `QueueCard` renders its CAP
 * stage's Review CTA disabled (with an explanatory tooltip) and `isItemReviewable`
 * keeps it out of the "Ready to review" counts. The card itself stays — it is
 * still dismissible.
 *
 * Scoped to items that would otherwise show a REVIEW row: a still-extracting or
 * already-reviewed CAP-only card has nothing to withhold.
 */
export function isCapImportContextMissing(item: AiProcessingItem): boolean {
  if (item.status === 'FAILED') return false;
  if (!isCapOnlyItem(item) || hasCapImportContext(item)) return false;
  return deriveAuditStages(item.steps).some(stage => stage.status === 'REVIEW');
}

/**
 * Projects the backend `item.steps` onto the visible audit stages.
 *
 * A stage's status is aggregated from its present member steps: any FAILED →
 * FAILED; else all finished (COMPLETED) → **REVIEW** if the stage is an
 * independent review entry (`reviewable`) that has NOT been reviewed yet,
 * **COMPLETED** if it is non-reviewable or already reviewed; else PROCESSING.
 * Its percent is the average of the present members.
 *
 * A finished *reviewable* stage is "ready to review", NOT "completed", until the
 * user reviews it: the backend then stamps a non-null `reviewedAt` on each of the
 * stage's member steps, and once EVERY present member is reviewed the stage flips
 * to COMPLETED (grey "done"). While unreviewed it renders a Review action (opening
 * `AuditAiDialog`, which resolves the correct landing step itself). A finished
 * NON-reviewable stage always shows "Completed".
 *
 * Failed-aware: when the overall workflow has FAILED (any step FAILED), finished
 * reviewable stages render as **COMPLETED** (grey "done"), NOT REVIEW — you can't
 * independently review a failed workflow's partial output, you retry first, so the
 * failed stage is the review/retry focus. The stage that failed stays FAILED.
 * `reviewedAt` does not override this failure logic.
 *
 * CAP-ONLY workflows (CAP extraction planned but no classification — the
 * CAP-import flow) are projected onto `CAP_ONLY_STAGE_PLAN` instead: a single
 * "CAP extraction" stage covering both of their steps, since they have no audit
 * to review.
 */
export function deriveAuditStages(
  steps: AuditProcessingStep[] | null | undefined,
): AuditStage[] {
  if (!steps || steps.length === 0) return [];
  const byKey = new Map(steps.map(step => [step.key, step]));
  const workflowFailed = steps.some(step => step.status === 'FAILED');
  const capOnly = isCapOnlyWorkflow(byKey);

  const stages: AuditStage[] = [];
  for (const { id, keys, reviewable } of capOnly
    ? CAP_ONLY_STAGE_PLAN
    : STAGE_PLAN) {
    const members = keys
      .map(key => byKey.get(key))
      .filter((step): step is AuditProcessingStep => step != null);
    if (members.length === 0) continue;

    let status: AuditStageStatus;
    if (members.some(m => m.status === 'FAILED')) {
      status = 'FAILED';
    } else if (members.every(m => m.status === 'COMPLETED')) {
      // A finished reviewable stage is ready to REVIEW until every present
      // member step has been reviewed (non-null `reviewedAt`), then COMPLETED.
      // On a failed workflow it renders COMPLETED regardless of reviewedAt.
      const reviewed = members.every(m => m.reviewedAt != null);
      status =
        reviewable && !workflowFailed && !reviewed ? 'REVIEW' : 'COMPLETED';
    } else {
      status = 'PROCESSING';
    }
    // Averaging the members is right when each one contributes real live
    // progress. The CAP-only stage is the exception: its raw-file EXTRACTION step
    // has no sub-progress (it jumps 0→100), so averaging would peg the bar at
    // ~50% for the whole extraction and then snap to 100. Track the CAP data
    // extraction alone there — the only step that reports granular progress.
    const percent = capOnly
      ? (byKey.get(CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION)
          ?.percent ?? 0)
      : Math.round(
          members.reduce((sum, m) => sum + m.percent, 0) / members.length,
        );
    stages.push({ id, status, percent });
  }

  return stages;
}

/**
 * Whether a queue item belongs in the "Ready to review" section: it keys off the
 * presence of a REVIEW stage (a finished, independently-reviewable, not-yet-
 * reviewed stage) so a card surfaces while it has anything left to review and
 * drops out once every reviewable stage is reviewed.
 *
 * For AUDIT items that have steps this is driven purely by `deriveAuditStages`:
 *  - a still-PROCESSING audit surfaces as soon as one visible stage becomes a
 *    REVIEW stage (a card with one reviewable stage surfaces while others process);
 *  - a fully-reviewed audit (no REVIEW stage remains) is NOT reviewable, even
 *    when COMPLETED.
 * It checks the derived STAGE, not a raw step: classification finishing while its
 * file EXTRACTION still runs keeps "Audit details" PROCESSING (nothing to review
 * yet).
 *
 * Non-AUDIT COMPLETED items are always reviewable, and an AUDIT item with NO steps
 * falls back to the flat-card rule (COMPLETED → reviewable). FAILED items are never
 * reviewable — nor is a CAP-only import whose row never recorded where the import
 * belongs (see {@link isCapImportContextMissing}): its Review CTA is disabled, so
 * it must not be advertised as ready to review either.
 */
export function isItemReviewable(item: AiProcessingItem): boolean {
  if (item.status === 'FAILED') return false;
  if (isCapImportContextMissing(item)) return false;
  if (item.assetType === 'AUDIT' && item.steps && item.steps.length > 0) {
    return deriveAuditStages(item.steps).some(
      stage => stage.status === 'REVIEW',
    );
  }
  return item.status === 'COMPLETED';
}

// Static per-step-key → i18n key map for the human-readable label of a single
// backend workflow step (finer-grained than the grouped `stage.*` labels). Keys
// are CAPA_WORKFLOW_TYPES values (imported from @retracedgmbh/modules, never bare
// literals); values are static i18n keys — no dynamic `step.${key}` lookups, per
// the no-dynamic-translation-keys convention. CAPA_ACTIONS_GENERATION is omitted
// on purpose: it's the internal AI-job type for action generation, not a plan
// step that appears in `item.steps` (the recorded step is CAPS_CREATION).
export const STEP_LABEL_KEYS: Record<string, string> = {
  [CAPA_WORKFLOW_TYPES.CAPA_CLASSIFICATION]: 'step.classification',
  [CAPA_WORKFLOW_TYPES.EXTRACTION]: 'step.extraction',
  [CAPA_WORKFLOW_TYPES.CAPA_CAP_FILE_DATA_EXTRACTION]:
    'step.capFileDataExtraction',
  [CAPA_WORKFLOW_TYPES.EXTRACT_FINDINGS]: 'step.extractFindings',
  [CAPA_WORKFLOW_TYPES.CAPS_CREATION]: 'step.capsCreation',
};

/**
 * Resolves a backend step `key` to its static i18n label key, or `undefined`
 * when the key has no user-facing label (an unmapped/internal type). Callers
 * pass the result to `getTranslation` — never build `step.${key}` dynamically.
 */
export function getStepLabelKey(stepKey: string): string | undefined {
  return STEP_LABEL_KEYS[stepKey];
}

export interface AuditStageCounts {
  running: number;
  completed: number;
  readyToReview: number;
}

export function countAuditStages(stages: AuditStage[]): AuditStageCounts {
  let running = 0;
  let completed = 0;
  let readyToReview = 0;
  for (const stage of stages) {
    if (stage.status === 'PROCESSING') running += 1;
    else if (stage.status === 'COMPLETED') completed += 1;
    else if (stage.status === 'REVIEW') readyToReview += 1;
  }
  return { running, completed, readyToReview };
}

// Steps NOT counted as user-facing jobs. The raw-parse EXTRACTION step is
// internal: it has no live progress (jumps 0→100, no per-step bar) and is folded
// into the "Audit details" row, so it never counts as a job of its own.
const NON_JOB_STEP_KEYS = new Set<string>([CAPA_WORKFLOW_TYPES.EXTRACTION]);

/**
 * Total number of jobs on the asset for the "N jobs" count: every planned step
 * that renders with its own progress bar — **including** steps not started yet
 * (PENDING) — **excluding** internal steps (EXTRACTION). Counts the real backend
 * steps in `item.steps`, NOT the grouped stage rows, so a card that folds
 * several steps into one row still reports the true job total. (Steps only enter
 * `item.steps` as the plan grows — e.g. findings are appended after "Get AI
 * suggestions" — so the total climbs as the backend plans more work.)
 */
export function countAuditJobs(
  steps: AuditProcessingStep[] | null | undefined,
): number {
  if (!steps) return 0;
  return steps.filter(step => !NON_JOB_STEP_KEYS.has(step.key)).length;
}
